import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../assets/js/LanguageContext';
import { localeOf } from '../assets/js/locale';
import AppModal from './common/Modal';
import Loader from './common/Loader';
import PriceCard from './common/PriceCard';
import SectionEyebrow from './common/SectionEyebrow';
import { PLANS, accountPlan, planHighlights } from './Pricing/plans';
import '../assets/styles/DashboardList.css';
import '../assets/styles/ChooseSurvey.css';
import '../assets/styles/Subscribe.css';

// IDs de precio reales de Stripe (modo live)
const PRICE_IDS = {
  basic: 'price_1Rs8bkJzYZl0kJvoGPRJzuUG',
  pro: 'price_1Rs8ekJzYZl0kJvovT1C0bkH',
  business: 'price_1Rs8hkJzYZl0kJvot89DHgXQ'
};

// Stripe se carga bajo demanda para no romper la pagina si Stripe.js esta
// bloqueado por el navegador o por alguna extension.
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51NIqFkJzYZl0kJvoq6x2RNZCNgL4qFiTwLAHvbJqxtYlMNUYNMgfuoRKRSbo5V461N8uxATyB3PjpZA2SYkSB9Ey00oTPntaCJ';
let stripePromise = null;

const getStripe = async () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

const SubscriptionOptions = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Plan elegido en Precios (?plan=pro): se marca en lugar del "más popular"
  const chosenPlan = PLANS.some((p) => p.key === searchParams.get('plan') && p.key !== 'free') ? searchParams.get('plan') : null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchUserSubscription = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) setUserSubscription(await response.json());
      } catch (err) {
        console.error('Error al obtener informacion de suscripcion:', err);
      }
    };

    fetchUserSubscription();
  }, [navigate]);

  const account = userSubscription ? accountPlan(userSubscription) : null;
  const canCancel = Boolean(account && account.isPaid);

  const handleCancelSubscription = async () => {
    setShowCancelConfirm(false);
    setCanceling(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cancel-subscription`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || t('subscription_cancel_error'));

      // El plan se mantiene hasta el final del periodo pagado; Stripe avisa al caducar
      setUserSubscription((prev) => ({ ...prev, subscription_status: 'canceled' }));
      setNotice({ variant: 'confirm', title: t('subscription_cancel'), text: t('subscription_cancel_success') });
    } catch (err) {
      console.error('Error al cancelar suscripcion:', err);
      setError(`${t('subscription_cancel_error')}: ${err.message}`);
    } finally {
      setCanceling(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('subscription_login_required'));
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: PRICE_IDS[planKey] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('subscription_checkout_error'));
      if (!data.sessionId) throw new Error(t('subscription_checkout_error'));

      const stripe = await getStripe();
      if (!stripe) throw new Error(t('subscription_stripe_unavailable'));

      const { error: stripeError } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      if (stripeError) throw new Error(stripeError.message);
    } catch (err) {
      console.error('Error al iniciar la suscripcion:', err);
      setError(`${t('subscription_checkout_error')}: ${err.message}`);
      setLoading(false);
    }
  };

  const choosePlan = (planKey) => {
    if (planKey === 'free') {
      setError(null);
      return;
    }
    handleSubscribe(planKey);
  };

  return (
    <div className="home-container tp-dash tp-subscribe">
      <div className="dashboard-container">
        <Link to="/profile" className="tp-choose__back">
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          {t('subscription_back')}
        </Link>

        <header className="tp-dash__head">
          <div className="tp-dash__heading">
            <SectionEyebrow>{t('subscription_eyebrow')}</SectionEyebrow>
            <h1 className="tp-subscribe__title">{t('subscription_title')}</h1>
            <p className="tp-dash__lead">{t('pricing_subtitle')}</p>
          </div>
        </header>

        {account && (
          <section className="tp-subscribe__current" aria-label={t('subscription_current_title')}>
            <span className="tp-subscribe__current-icon" aria-hidden="true">
              <CreditCard size={20} strokeWidth={1.9} />
            </span>
            <div className="tp-subscribe__current-text">
              <p className="tp-subscribe__current-label">{t('subscription_current_title')}</p>
              <p className="tp-subscribe__current-plan">
                {account.isRole ? t(`account_role_${account.key}`) : t(`pricing_${account.key}`)}
                {account.isRole ? (
                  <span className="tp-subscribe__status is-active">{t('account_full_access')}</span>
                ) : account.viaCode ? (
                  <span className="tp-subscribe__status is-active">
                    {account.expiresAt
                      ? t('account_via_code_until').replace('{date}', account.expiresAt.toLocaleDateString(localeOf(language), { day: 'numeric', month: 'long', year: 'numeric' }))
                      : t('account_via_code')}
                  </span>
                ) : account.isPaid || account.canceled ? (
                  <span className={`tp-subscribe__status ${account.canceled ? 'is-canceled' : 'is-active'}`}>
                    {account.canceled ? t('subscription_status_canceled') : t('subscription_status_active')}
                  </span>
                ) : null}
              </p>
              {account.isRole && <p className="tp-subscribe__current-note">{t('account_role_note')}</p>}
            </div>
            {canCancel && (
              <button
                type="button"
                className="btn btn--outline btn--sm tp-subscribe__cancel"
                onClick={() => setShowCancelConfirm(true)}
                disabled={canceling}
              >
                {canceling ? t('subscription_canceling') : t('subscription_cancel')}
              </button>
            )}
          </section>
        )}

        {error && <p className="tp-form__alert" role="alert">{error}</p>}

        <div className="tp-price-grid tp-subscribe__grid">
          {PLANS.map((plan) => {
            const isCurrent = Boolean(account && !account.isRole && account.key === plan.key);
            const includedInRole = Boolean(account && account.isRole);
            let ctaText = t(`pricing_${plan.key}_btn`);
            if (includedInRole) ctaText = t('account_included');
            else if (isCurrent) ctaText = t('subscription_current_plan');
            return (
              <PriceCard
                key={plan.key}
                data-plan={plan.key}
                name={t(`pricing_${plan.key}`)}
                price={plan.price}
                period={t('pricing_month')}
                features={[
                  t(`pricing_${plan.key}_diagnosticos`),
                  t(`pricing_${plan.key}_participantes`),
                  t(`pricing_${plan.key}_emotions`),
                  ...planHighlights(plan.key).map((key) => t(key))
                ]}
                ctaText={ctaText}
                ctaDisabled={isCurrent || includedInRole || loading}
                onCta={() => choosePlan(plan.key)}
                featured={chosenPlan ? plan.key === chosenPlan : plan.featured}
                badge={chosenPlan ? t('subscription_chosen_plan') : t('pricing_most_popular')}
              />
            );
          })}
        </div>

        <p className="tp-subscribe__notice">
          <ShieldCheck size={16} strokeWidth={2} aria-hidden="true" />
          {t('subscription_stripe_notice')}
        </p>

        {loading && <Loader overlay caption={t('subscription_redirecting')} />}

        <AppModal
          open={showCancelConfirm}
          onOpenChange={(open) => { if (!open) setShowCancelConfirm(false); }}
          variant="delete"
          title={t('subscription_cancel')}
          description={t('subscription_cancel_confirm')}
          cancelText={t('common_cancel')}
          confirmText={t('subscription_cancel')}
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
        />

        <AppModal
          open={Boolean(notice)}
          onOpenChange={(open) => { if (!open) setNotice(null); }}
          variant={notice ? notice.variant : 'info'}
          title={notice ? notice.title : ''}
          description={notice ? notice.text : ''}
          confirmText={t('common_understood')}
          onConfirm={() => setNotice(null)}
        />
      </div>
    </div>
  );
};

export default SubscriptionOptions;
